"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Globe,
  PhoneOutgoing,
  Clock,
  GitBranch,
  FileBarChart,
  ShieldCheck,
  TrendingDown,
  ChevronDown,
  Zap,
} from "lucide-react";

const features = [
  {
    icon: Zap,
    title: "Same-Day Custom Setup",
    desc: "We build your agent around your business: your services, hours, pricing, and booking flow. Tell us how you work and we tailor the script, voice, and call handling to match. Most clients are live the same day they sign up.",
  },
  {
    icon: Globe,
    title: "Multilingual Voice Outreach",
    desc: "Break language barriers with voice agents that support diverse audiences and help teams expand their reach across regions and demographics.",
  },
  {
    icon: PhoneOutgoing,
    title: "Automated Dialing at Scale",
    desc: "Run outreach programs, follow-ups, reminders, confirmations, and qualification flows without relying on manual calling teams.",
  },
  {
    icon: Clock,
    title: "24/7 Availability",
    desc: "Keep campaigns moving after hours, across time zones, and during peak periods without staffing interruptions or delays.",
  },
  {
    icon: GitBranch,
    title: "Flexible Campaign Workflows",
    desc: "Support lead qualification, customer recontact, appointment reminders, research recruitment, and custom outreach paths, all configurable.",
  },
  {
    icon: FileBarChart,
    title: "Reporting and Exports",
    desc: "Track call outcomes, export results, and review performance with practical reporting tools designed for operational teams.",
  },
  {
    icon: ShieldCheck,
    title: "Human Oversight",
    desc: "Gain confidence through project support, structured onboarding, and a clear operational process backed by experienced project managers.",
  },
  {
    icon: TrendingDown,
    title: "Smarter Cost Structure",
    desc: "Monthly plans unlock lower per-minute rates for higher-volume usage, giving growing teams predictable costs that improve with scale.",
  },
];

const supportedLanguages = [
  "English (US, India, UK, Australia, New Zealand)",
  "Spanish (Spain, Latin America)",
  "French (Canada, France)",
  "Chinese",
  "German",
  "Hindi",
  "Japanese",
  "Portuguese (Portugal, Brazil)",
  "Russian",
  "Italian",
  "Korean",
  "Dutch",
  "Dutch (Belgium)",
  "Polish",
  "Turkish",
  "Vietnamese",
  "Romanian",
  "Danish",
  "Finnish",
  "Greek",
  "Indonesian",
  "Norwegian",
  "Slovak",
  "Swedish",
  "Bulgarian",
  "Hungarian",
  "Malay",
  "Catalan",
  "Thai",
  "Latvian",
  "Czech",
  "Lithuanian",
  "Arabic",
  "Afrikaans (South Africa)",
  "Azerbaijani",
  "Bosnian",
  "Welsh",
  "Persian",
  "Filipino",
  "Galician (Spain)",
  "Hebrew",
  "Croatian",
  "Armenian",
  "Icelandic",
  "Kazakh",
  "Kannada (India)",
  "Marathi (India)",
  "Tamil",
  "Macedonian (North Macedonia)",
  "Nepali",
  "Slovenian",
  "Serbian",
  "Swahili",
  "Urdu",
  "Ukrainian",
];

export default function Features() {
  const [langOpen, setLangOpen] = useState(false);

  return (
    <section id="features" className="section-padding" style={{ background: "var(--color-bg)" }}>
      <div className="section-inner">
        <h2 className="section-heading">
          Voice automation built for{" "}
          <em style={{ fontFamily: "var(--font-heading)" }}>real business workflows</em>
        </h2>
        <p className="section-subheading">
          Launch AI voice campaigns that are easier to manage, easier to scale,
          and easier to measure.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 24,
          }}
        >
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              className="card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.06 }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  background: "var(--color-bg-alt)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 20,
                }}
              >
                <f.icon size={22} color="var(--color-text)" strokeWidth={1.6} />
              </div>
              <h3
                style={{
                  fontFamily: "var(--font-heading)",
                  fontSize: "1.25rem",
                  marginBottom: 10,
                  color: "var(--color-text)",
                }}
              >
                {f.title}
              </h3>
              <p
                style={{
                  fontSize: "0.92rem",
                  color: "var(--color-text-muted)",
                  fontWeight: 300,
                  lineHeight: 1.7,
                }}
              >
                {f.desc}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Expandable languages list */}
        <div style={{ marginTop: 40, textAlign: "center" }}>
          <button
            onClick={() => setLangOpen(!langOpen)}
            style={{
              background: "none",
              border: "1px solid var(--color-border)",
              borderRadius: 10,
              padding: "10px 20px",
              color: "var(--color-text-muted)",
              fontFamily: "var(--font-body)",
              fontSize: "0.85rem",
              fontWeight: 400,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              transition: "all 0.2s ease",
            }}
          >
            <Globe size={14} />
            {langOpen ? "Hide" : "View"} supported languages ({supportedLanguages.length})
            <motion.span
              animate={{ rotate: langOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              style={{ display: "flex" }}
            >
              <ChevronDown size={14} />
            </motion.span>
          </button>

          <AnimatePresence>
            {langOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                style={{ overflow: "hidden" }}
              >
                <div
                  style={{
                    marginTop: 20,
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "8px 16px",
                    maxWidth: 700,
                    margin: "20px auto 0",
                  }}
                >
                  {supportedLanguages.map((lang) => (
                    <span
                      key={lang}
                      style={{
                        fontFamily: "var(--font-body)",
                        fontSize: "0.82rem",
                        color: "var(--color-text-muted)",
                        fontWeight: 300,
                        padding: "4px 12px",
                        borderRadius: 6,
                        background: "var(--color-bg-alt)",
                        border: "1px solid var(--color-border)",
                      }}
                    >
                      {lang}
                    </span>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
